import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { universalSearch, memoryVaultAsk } from "@/lib/api";
import {
  Search, Mail, ClipboardCheck, CalendarClock, Trophy, FileText, Users, Loader2, Sparkles,
} from "lucide-react";
import PageErrorBoundary from "@/components/PageErrorBoundary";
import FeedbackBar from "@/components/FeedbackBar";
import useTrackPage from "@/hooks/useTrackPage";

const TYPE_META = {
  emails:      { Icon: Mail, label: "Emails" },
  tasks:       { Icon: ClipboardCheck, label: "Tasks" },
  events:      { Icon: CalendarClock, label: "Events" },
  milestones:  { Icon: Trophy, label: "Milestones" },
  attachments: { Icon: FileText, label: "Attachments" },
  children:    { Icon: Users, label: "Child profiles" },
};

function SearchInner() {
  useTrackPage("search_open");
  const [params, setParams] = useSearchParams();
  const q0 = params.get("q") || "";
  const mode0 = params.get("mode") || "search";
  const [q, setQ] = useState(q0);
  const [mode, setMode] = useState(mode0);
  const [data, setData] = useState(null);
  const [askData, setAskData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!q0 || q0.length < 2) {
      setData(null);
      setAskData(null);
      return;
    }
    setLoading(true);
    if (mode0 === "ask") {
      memoryVaultAsk(q0)
        .then(setAskData).catch(() => setAskData(null))
        .finally(() => setLoading(false));
    } else {
      universalSearch(q0)
        .then(setData).catch(() => setData(null))
        .finally(() => setLoading(false));
    }
  }, [q0, mode0]);

  const onSubmit = (e) => {
    e.preventDefault();
    const term = q.trim();
    const np = { q: term };
    if (mode === "ask") np.mode = "ask";
    setParams(np);
  };

  const switchMode = (next) => {
    setMode(next);
    const np = {};
    if (q0) np.q = q0;
    if (next === "ask") np.mode = "ask";
    setParams(np);
  };

  const exampleQuestions = [
    "When did soccer start?",
    "Honor Roll wins",
    "When was the last dentist appointment?",
    "What activities happened in 2024?",
  ];

  return (
    <div data-testid="search-page" className="space-y-6">
      <header className="fcc-card p-6 sm:p-8">
        <p className="label-overline flex items-center gap-2"><Search className="w-3 h-3" /> Family Memory Vault</p>
        <h1 className="font-display text-3xl text-[var(--fcc-ink)] mt-2">Find anything about your family</h1>
        <p className="text-sm text-[var(--fcc-ink-soft)] mt-2">
          Searches across emails, tasks, events, milestones, attachments, and child profiles.
        </p>
        <div className="mt-4 flex gap-2" data-testid="memory-vault-tabs">
          <button
            data-testid="vault-tab-search"
            onClick={() => switchMode("search")}
            className={`text-sm font-semibold px-4 py-2 rounded-xl transition ${
              mode === "search"
                ? "bg-[var(--fcc-ink)] text-white"
                : "bg-white border border-[var(--fcc-border)] text-[var(--fcc-ink-soft)] hover:bg-[var(--fcc-cream-2)]"
            }`}
          >
            <Search className="w-3.5 h-3.5 inline-block mr-1.5" />
            Keyword search
          </button>
          <button
            data-testid="vault-tab-ask"
            onClick={() => switchMode("ask")}
            className={`text-sm font-semibold px-4 py-2 rounded-xl transition ${
              mode === "ask"
                ? "bg-[var(--fcc-ink)] text-white"
                : "bg-white border border-[var(--fcc-border)] text-[var(--fcc-ink-soft)] hover:bg-[var(--fcc-cream-2)]"
            }`}
          >
            <Sparkles className="w-3.5 h-3.5 inline-block mr-1.5" />
            Ask family memory
          </button>
        </div>
        <form onSubmit={onSubmit} className="mt-4 flex gap-2">
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={
              mode === "ask"
                ? "Ask anything · e.g. When did Dev first start soccer?"
                : "Try: soccer, report card, science fair, Dr Patel…"
            }
            className="fcc-input flex-1"
            autoFocus
          />
          <button type="submit" data-testid="search-submit" disabled={q.trim().length < 2} className="fcc-btn-primary text-sm py-2.5 px-5">
            {mode === "ask" ? "Ask" : "Search"}
          </button>
        </form>
        {mode === "ask" && !q0 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="vault-example-questions">
            {exampleQuestions.map((ex) => (
              <button
                key={ex}
                data-testid="vault-example-chip"
                onClick={() => { setQ(ex); setParams({ q: ex, mode: "ask" }); }}
                className="fcc-pill text-xs bg-white border border-[var(--fcc-border)] text-[var(--fcc-ink-soft)] hover:bg-[var(--fcc-cream-2)] transition"
              >
                {ex}
              </button>
            ))}
          </div>
        )}
      </header>

      {loading ? (
        <div className="fcc-card p-10 text-center text-[var(--fcc-muted)]">
          <Loader2 className="w-5 h-5 mx-auto animate-spin" />
          <p className="text-sm mt-3">{mode === "ask" ? "Thinking…" : "Scanning your family vault…"}</p>
        </div>
      ) : mode === "ask" ? (
        !askData ? (
          <div className="fcc-card p-10 text-center text-sm text-[var(--fcc-muted)]">
            Ask a question above. KidHQ will search your family&apos;s vault and return matching records.
          </div>
        ) : (
          <AskResults askData={askData} />
        )
      ) : !data ? (
        <div className="fcc-card p-10 text-center text-sm text-[var(--fcc-muted)]">
          Type at least 2 characters to search.
        </div>
      ) : data.total === 0 ? (
        <div className="fcc-card p-10 text-center text-sm text-[var(--fcc-muted)]" data-testid="search-empty">
          No matches for &ldquo;{data.q}&rdquo;. Try a different word or shorter phrase.
        </div>
      ) : (
        <div className="space-y-6">
          <p className="text-xs uppercase tracking-widest text-[var(--fcc-muted)]" data-testid="search-summary">
            {data.total} match{data.total === 1 ? "" : "es"} for &ldquo;{data.q}&rdquo;
          </p>
          {Object.entries(data.results).map(([type, rows]) => (
            rows.length > 0 && (
              <ResultGroup key={type} type={type} rows={rows} />
            )
          ))}
        </div>
      )}
    </div>
  );
}

function AskResults({ askData }) {
  const total = askData.total || 0;
  const types = askData?.intent?.types_inferred || "";
  return (
    <div className="space-y-6" data-testid="vault-ask-results">
      <section className="fcc-card-accent p-6 sm:p-7">
        <p className="label-overline flex items-center gap-2"><Sparkles className="w-3 h-3" /> Memory Vault answer</p>
        <p className="font-display text-2xl text-[var(--fcc-ink)] mt-2" data-testid="vault-ask-answer">
          Found {total} record{total === 1 ? "" : "s"} for &ldquo;{askData.q}&rdquo;
        </p>
        {types && (
          <p className="text-xs text-[var(--fcc-ink-soft)] mt-2">
            Searched: <span className="font-semibold">{types.replaceAll(",", ", ")}</span>
          </p>
        )}
        {total === 0 && (
          <p className="text-sm text-[var(--fcc-ink-soft)] mt-3">
            No matches yet. Try a different phrasing, or check Memory Vault search for broader results.
          </p>
        )}
        <div className="mt-4 pt-3 border-t border-[var(--fcc-border)]">
          <FeedbackBar surface="memory_vault" targetId={`vault-ask:${askData.q}`} />
        </div>
      </section>
      {total > 0 && (
        <div className="space-y-6" data-testid="vault-ask-sources">
          <p className="text-xs uppercase tracking-widest text-[var(--fcc-muted)]">Sources</p>
          {Object.entries(askData.results || {}).map(([type, rows]) => (
            rows.length > 0 && <ResultGroup key={type} type={type} rows={rows} />
          ))}
        </div>
      )}
    </div>
  );
}

function ResultGroup({ type, rows }) {
  const meta = TYPE_META[type] || { Icon: Search, label: type };
  const Icon = meta.Icon;
  return (
    <section data-testid={`search-group-${type}`} className="fcc-card p-6">
      <header className="flex items-center gap-2 mb-3">
        <span className="inline-flex w-9 h-9 rounded-xl bg-[var(--fcc-cream-2)] items-center justify-center">
          <Icon className="w-4 h-4 text-[var(--fcc-green)]" />
        </span>
        <h2 className="font-display text-xl text-[var(--fcc-ink)]">{meta.label}</h2>
        <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)] ml-2">{rows.length}</span>
      </header>
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={`${type}-${r.id}`} data-testid="search-result-row">
            <Link
              to={r.deeplink || "#"}
              className="rounded-xl border border-[var(--fcc-border)] p-3.5 flex items-start gap-3 hover:bg-[var(--fcc-cream-2)] transition"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="font-semibold text-sm text-[var(--fcc-ink)] truncate">{r.title || "(untitled)"}</p>
                  {r.at && <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)] shrink-0">{(r.at || "").slice(0, 10)}</span>}
                </div>
                {r.snippet && <p className="text-xs text-[var(--fcc-ink-soft)] mt-0.5 truncate">{r.snippet}</p>}
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  {r.badge && <span className="fcc-pill text-[10px] bg-[var(--fcc-cream-2)] text-[var(--fcc-ink)]">{r.badge}</span>}
                  {r.child_name && <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)]">{r.child_name}</span>}
                  {r.done && <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-green)]">done</span>}
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function SearchPage() {
  return (
    <PageErrorBoundary scope="search">
      <SearchInner />
    </PageErrorBoundary>
  );
}
